<script setup lang="ts">
import { computed } from 'vue';
import { resolveEffectiveType } from '@Src/shared/utils/resolveEffectiveType';
import { formatLongDate } from '@Src/shared/utils/formatDate';
import BottomSheetModal from '@Components/BottomSheetModal.vue';
import { getBasePath } from '@Src/mfe.config';
import CertifidLogo from '@Components/legal/CertifidLogo.vue';
import { useCompanyStore } from '@Stores/company.store';
import { useDashboardStore } from '@Stores/dashboard.store';
import { CompanyLogoDisplay } from '@Shared';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useDealLogoUrl from '@Use/useDealLogoUrl';
import type { TaskItem } from '@Types/dashboard.types';
import type { PrimingConfig } from '@Components/priming/primingConfigs';

const props = defineProps<{
    open: boolean;
    task: TaskItem | null;
    config: PrimingConfig | null;
    isLoading?: boolean;
    error?: string | null;
}>();

const emit = defineEmits<{
    'update:open': [value: boolean];
    continue: [];
}>();

const companyStore = useCompanyStore();
const dashboardStore = useDashboardStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyLogoUrl = useDealLogoUrl(companyImageBaseUrl, companyStore, dashboardStore);

const hasIllustration = computed(() => !!props.config?.illustration);

const isRepayment = computed(() => props.task ? resolveEffectiveType(props.task) === 'payment_repayment' : false);

// Payment amount + closing date — hydrated by the BFF (TaskReconciliationBusiness)
// onto taskData as flat keys: `amount` (number) and `closing_date` (YYYY-MM-DD).
const paymentAmount = computed<number | null>(() => {
    const amount = props.task?.taskData?.amount;
    return typeof amount === 'number' ? amount : null;
});

const formattedAmount = computed(() => {
    if (paymentAmount.value == null) return '';
    // Show cents only when the amount has a fractional part: whole dollars render
    // as "$1,500", amounts with cents render as "$1.23" / "$1,500.50".
    const hasCents = !Number.isInteger(paymentAmount.value);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
    }).format(paymentAmount.value);
});

const closingDate = computed(() => {
    const raw = props.task?.taskData?.closing_date as string | undefined;
    if (!raw) return '';
    // Parse bare YYYY-MM-DD as local midnight to avoid UTC→local drift in
    // negative-offset timezones. Same pattern the prototype uses.
    let date: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, d] = raw.split('-').map(Number) as [number, number, number];
        date = new Date(y, m - 1, d);
    } else {
        date = new Date(raw);
    }
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
});

// Co-branded legal pages live at the client hub origin under the company's
// slug (DS-4798); the host injects the slug-prefixed base path. Fall back to
// the corporate pages if the base path is unavailable, so the link never 404s.
// Same pattern as WelcomeSheet.
const legalTermsUrl = computed(() => {
    const base = getBasePath();
    return base ? `${base}/legal/terms` : 'https://www.certifid.com/company/terms-of-service';
});
const legalPrivacyUrl = computed(() => {
    const base = getBasePath();
    return base ? `${base}/legal/privacy` : 'https://www.certifid.com/company/privacy-policy';
});

const sentDate = computed(() => {
    const raw = props.task?.taskData?.original_payment_date;
    return typeof raw === 'string' ? formatLongDate(raw) : '';
});

const companyName = computed(() =>
    companyStore.company?.dbaName || companyStore.company?.name || 'the title company',
);

const repaymentExplanation = computed(() => {
    const company = companyName.value;
    const opener = sentDate.value
        ? `The payment you sent on ${sentDate.value} was returned by your bank.`
        : `Your bank returned the payment we sent on your behalf.`;
    return `${opener} We covered the funds with ${company} so your closing wasn’t delayed. Now you’ll resubmit your payment directly to us.`;
});

const handleClose = () => emit('update:open', false);
const handleContinue = () => emit('continue');
</script>

<template>
    <BottomSheetModal
        :open="open"
        :overlay-handle="hasIllustration"
        :dialog-label="config?.title ?? 'Task details'"
        @update:open="(val: boolean) => { if (!val) handleClose(); }"
    >
        <div v-if="config" class="tps" :class="{ 'tps--illustrated': hasIllustration }">
            <component :is="config.illustration" v-if="config.illustration" class="tps__illustration" />

            <div class="tps__body">
                <!-- Co-brand lockup for repayment entry -->
                <div v-if="isRepayment && companyStore.company" class="tps__cobrand" data-test="repayment-cobrand">
                    <div class="tps__cobrand-left">
                        <CertifidLogo />
                        <span class="tps__cobrand-divider" aria-hidden="true"></span>
                    </div>
                    <CompanyLogoDisplay
                        :company-logo-url="companyLogoUrl"
                        :company="companyStore.company"
                        :brand="companyStore.brand"
                        class="tps__cobrand-company"
                    />
                </div>
                <div v-else-if="isRepayment" class="tps__cobrand tps__cobrand--fallback" data-test="repayment-cobrand">
                    <CertifidLogo />
                </div>

                <!-- Repayment leads action-first: headline before the amount block. -->
                <template v-if="isRepayment">
                    <div class="tps__heading">
                        <h2 class="tps__title">{{ config.title }}</h2>
                    </div>
                    <p class="tps__repayment-explanation" data-test="repayment-explanation">{{ repaymentExplanation }}</p>
                </template>

                <!-- Payment amount block (payment_emd / payment_c2c / payment_repayment) -->
                <div v-if="paymentAmount != null" class="tps__amount">
                    <span class="tps__amount-label">Amount Due</span>
                    <p class="tps__amount-value" data-test="priming-amount-value">{{ formattedAmount }}</p>
                    <div v-if="!isRepayment && closingDate" class="tps__amount-meta">
                        <app-icon icon="cd-ic-calendar" class="tps__amount-meta-icon" />
                        <span>Closing {{ closingDate }}</span>
                    </div>
                </div>

                <div v-if="!isRepayment" class="tps__heading">
                    <h2 class="tps__title">{{ config.title }}</h2>
                    <p v-if="config.subtitle" class="tps__subtitle">{{ config.subtitle }}</p>
                </div>

                <ul v-if="!isRepayment && config.items && config.items.length > 0" class="tps__items" :class="{ 'tps__items--bordered': hasIllustration }">
                    <li v-for="(item, index) in config.items" :key="index" class="tps__item">
                        <span v-if="item.illustration" class="tps__item-illustration">
                            <component :is="item.illustration" />
                        </span>
                        <span v-else class="tps__item-icon">
                            <app-icon :icon="item.icon" />
                        </span>
                        <div class="tps__item-text">
                            <p v-if="!hasIllustration && item.label" class="tps__item-label">{{ item.label }}</p>
                            <p class="tps__item-description">{{ item.description }}</p>
                        </div>
                    </li>
                </ul>

                <div v-if="config.warning" class="tps__warning">
                    <app-icon icon="cd-ic-alert-triangle" class="tps__warning-icon" />
                    <div>
                        <p class="tps__warning-title">{{ config.warning.title }}</p>
                        <p class="tps__warning-description">{{ config.warning.description }}</p>
                    </div>
                </div>

                <p v-if="error" class="tps__error" role="alert">{{ error }}</p>

                <app-button
                    class="tps__cta"
                    expanded
                    size="large"
                    type="primary"
                    :is-loading="isLoading"
                    @onClick="handleContinue"
                >
                    {{ config.ctaLabel }}
                </app-button>

                <a
                    v-if="config.helpLink"
                    class="tps__help-link"
                    :href="config.helpLink.url"
                    target="_blank"
                    rel="noopener noreferrer"
                >{{ config.helpLink.label }}</a>

                <p v-if="config.termsLinkUrl" class="tps__terms">
                    {{ config.termsLabel ?? 'By continuing, you accept the linked terms.' }}
                    <a :href="config.termsLinkUrl" target="_blank" rel="noopener noreferrer">Learn more</a>
                </p>

                <p v-if="config.showLegalConsent" class="tps__terms">
                    By clicking ‘{{ config.ctaLabel }}’ you agree to our
                    <a :href="legalTermsUrl" target="_blank" rel="noopener noreferrer">Terms of Use</a>
                    and
                    <a :href="legalPrivacyUrl" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
            </div>
        </div>
    </BottomSheetModal>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.tps {
    display: flex;
    flex-direction: column;

    // Illustrated mode: the BottomSheetModal content has default padding that
    // would push the illustration inward. Cancel that so the illustration
    // spans the sheet width, then apply the body padding manually.
    &--illustrated {
        margin: calc(-1 * #{$portal-section-py}) calc(-1 * #{$portal-section-px}) 0;

        @media (min-width: 768px) {
            margin: -24px -32px 0;
        }
    }

    &__illustration {
        flex-shrink: 0;
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 20px $portal-section-px 0;

        @media (min-width: 768px) {
            padding: 20px 32px 0;
        }
    }

    // --- Co-brand lockup (repayment entry) ---
    &__cobrand {
        display: flex;
        align-items: center;
        gap: 14px;
        flex-wrap: wrap;

        &--fallback {
            justify-content: center;
        }
    }

    // Keep the CertifID wordmark and divider together so only the company logo
    // can wrap to a second row on narrow screens.
    &__cobrand-left {
        display: flex;
        align-items: center;
        gap: 14px;
        flex-shrink: 0;
    }

    &__cobrand-divider {
        width: 1px;
        height: 24px;
        background: $portal-border;
        flex-shrink: 0;
    }

    &__cobrand-company {
        max-width: 180px;
    }

    // --- Repayment explanation ---
    &__repayment-explanation {
        font-size: $text-portal-body;
        color: $portal-text;
        line-height: 1.5;
        margin: 0;
    }

    // --- Amount block (payment tasks) ---
    &__amount {
        text-align: center;
        padding-bottom: 16px;
        border-bottom: 1px solid $portal-border;
    }

    &__amount-label {
        font-size: $text-portal-small;
        font-weight: 600;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: $portal-text-mid;
    }

    &__amount-value {
        font-size: 32px;
        font-weight: 700;
        margin: 4px 0 0;
        color: var(--ch-primary, #156FBE);
        line-height: 1.2;
    }

    &__amount-meta {
        margin-top: 10px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: $text-portal-caption;
        color: $portal-text-mid;
    }

    &__amount-meta-icon {
        width: 14px;
        height: 14px;
    }

    // --- Heading ---
    &__heading {
        // Centered by default; left-aligned when an illustration is present
        // (matches prototype's illustration-mode layout).
        text-align: center;
    }

    &--illustrated &__heading {
        text-align: left;
    }

    &__title {
        font-size: $text-portal-h3;
        font-weight: 700;
        color: $portal-text;
        line-height: 1.3;
        margin: 0;
    }

    &__subtitle {
        font-size: $text-portal-caption;
        color: $portal-text-mid;
        line-height: 1.5;
        margin: 8px 0 0;
    }

    &--illustrated &__subtitle {
        font-size: $text-portal-body-lg;
        color: $portal-text;
    }

    // --- Items list ---
    &__items {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: $portal-item-gap;
        background: $portal-bg;
        border-radius: $portal-surface-card-radius;
        padding: $portal-section-py;

        &--bordered {
            background: transparent;
            border: 1px solid $portal-border;
            border-radius: $portal-card-radius;
            padding: $portal-card-padding;
            gap: 12px;
        }
    }

    &__item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    &--illustrated &__item {
        align-items: center;
    }

    &__item-icon {
        flex-shrink: 0;
        width: $portal-priming-icon;
        height: $portal-priming-icon;
        border-radius: 12px;
        background: var(--ch-primary-lightest, #DEEFF9);
        color: var(--ch-primary, #156FBE);
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            width: calc(#{$portal-priming-icon} * 0.45);
            height: calc(#{$portal-priming-icon} * 0.45);
        }
    }

    // Per-item custom illustration (DS-5345). Renders the provided component
    // in place of the standard <app-icon> when PrimingItem.illustration is set.
    &__item-illustration {
        flex-shrink: 0;
        width: 64px;
        display: flex;
        align-items: center;
        justify-content: center;

        :deep(svg) {
            max-width: 100%;
            height: auto;
        }
    }

    &--illustrated &__item-icon {
        width: 40px;
        height: 40px;
        border-radius: 50%;

        svg {
            width: 20px;
            height: 20px;
        }
    }

    &__item-text {
        flex: 1;
        min-width: 0;
    }

    &__item-label {
        font-size: $text-portal-body-lg;
        font-weight: 700;
        color: $portal-text;
        margin: 0 0 4px;
        line-height: 1.4;
    }

    &__item-description {
        font-size: $text-portal-caption;
        color: $portal-text-mid;
        margin: 0;
        line-height: 1.5;
    }

    // --- Warning card ---
    &__warning {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px;
        border: 1px solid #F5C451;
        border-radius: $portal-card-radius;
    }

    &__warning-icon {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        margin-top: 2px;
        color: #D9821B;
    }

    &__warning-title {
        font-size: $text-portal-small;
        font-weight: 700;
        color: $portal-text;
        margin: 0 0 2px;
    }

    &__warning-description {
        font-size: $text-portal-small;
        color: $portal-text-mid;
        margin: 0;
        line-height: 1.5;
    }

    // --- CTA ---
    // No overrides: padding/height/alignment all live inside <app-button>,
    // and the body's flex gap handles spacing above/below it.

    &__error {
        font-size: $text-portal-small;
        color: $red;
        margin: 0;
        text-align: center;
    }

    // --- Help link (standalone, e.g. IDV "Why am I doing this?") ---
    &__help-link {
        font-size: $text-portal-small;
        color: var(--ch-primary, #156FBE);
        text-decoration: underline;
        text-align: center;
        align-self: center;
    }

    // --- Terms ---
    &__terms {
        font-size: $text-portal-small;
        color: $portal-text-mid;
        text-align: center;
        margin: 0;
        line-height: 1.5;

        a {
            color: var(--ch-primary, #156FBE);
            text-decoration: underline;
        }
    }
}
</style>
